import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { LogoDownload } from '../components/LogoDownload';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  LogOut,
  Search,
  Filter,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  MoreVertical,
  RefreshCw,
  Palette,
  Download,
  Trash2,
  Settings,
  X,
  Loader2,
  User,
  Lock,
  Menu,
  FileEdit,
  FilePlus,
  MapPin,
  ExternalLink,
  FolderOpen,
  Upload,
  Copy,
  Link2,
  Building2,
  Pencil,
  Receipt
} from 'lucide-react';

// Lazy load PDF Editor for better performance
const PDFEditor = lazy(() => import('../components/PDFEditor'));
const MarketEditor = lazy(() => import('../components/MarketEditor'));
const RateCardManager = lazy(() => import('../components/rates/RateCardManager'));

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ total: 0, complete: 0, incomplete: 0, drafts: 0 });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLogoDownload, setShowLogoDownload] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showPDFEditor, setShowPDFEditor] = useState(false);
  const [showDocCreator, setShowDocCreator] = useState(false);
  const [showMarketEditor, setShowMarketEditor] = useState(false);
  const [showRateCards, setShowRateCards] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickLink, setQuickLink] = useState({ url: '', label: '' });
  const [adminDocs, setAdminDocs] = useState([]);
  const [bankLinkTitle, setBankLinkTitle] = useState('Bank Info for Techs');
  const [editingBankTitle, setEditingBankTitle] = useState(false);
  const [bankTitleInput, setBankTitleInput] = useState('');
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchQuickLink = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/profile`, { headers: getAuthHeaders() });
      if (res.data.quick_link_url) {
        setQuickLink({ url: res.data.quick_link_url, label: res.data.quick_link_label || 'Quick Link' });
      }
    } catch { toast.error('Could not load your Quick Link'); }
  }, [getAuthHeaders]);

  const fetchAdminDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/docs`, { headers: getAuthHeaders() });
      setAdminDocs(res.data || []);
    } catch { toast.error('Could not load your documents'); }
  }, [getAuthHeaders]);

  const fetchBankLinkSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/bank-link-settings`, { headers: getAuthHeaders() });
      if (res.data?.title) setBankLinkTitle(res.data.title);
    } catch { toast.error('Could not load Bank Info settings'); }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchQuickLink();
    fetchAdminDocs();
    fetchBankLinkSettings();
  }, [fetchQuickLink, fetchAdminDocs, fetchBankLinkSettings]);

  const saveBankLinkTitle = async () => {
    try {
      await axios.put(`${API_URL}/admin/bank-link-settings`, { title: bankTitleInput }, { headers: getAuthHeaders() });
      setBankLinkTitle(bankTitleInput);
      setEditingBankTitle(false);
      toast.success('Link title updated');
    } catch (error) { toast.error('Failed to save'); }
  };

  const bankInfoUrl = `${window.location.origin}/bank-info`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/applications${statusParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/stats`, { headers: getAuthHeaders() })
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const closeAll = () => {
      setShowPDFEditor(false);
      setShowMarketEditor(false);
      setShowRateCards(false);
      setShowLogoDownload(false);
      setShowSettings(false);
      setShowDeleteConfirm(null);
      setShowDocCreator(false);
      setSidebarOpen(false);
    };
    window.addEventListener('admin:home', closeAll);
    return () => window.removeEventListener('admin:home', closeAll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteApplication = async (appId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/applications/${appId}`, { headers: getAuthHeaders() });
      toast.success('Application deleted successfully');
      setShowDeleteConfirm(null);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error('Failed to delete application');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <span className="status-badge status-draft">Draft</span>;
      case 'submitted':
        return <span className="status-badge status-submitted">Submitted</span>;
      case 'incomplete':
        return <span className="status-badge status-incomplete">Incomplete</span>;
      case 'complete':
        return <span className="status-badge status-complete">Complete</span>;
      default:
        return <span className="status-badge status-draft">{status}</span>;
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      app.user_name?.toLowerCase().includes(searchLower) ||
      app.user_email?.toLowerCase().includes(searchLower) ||
      app.data?.phone?.includes(searchTerm)
    );
  });

  return (
    <div className="own-type-scale min-h-screen bg-[#0A0A0F] flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 bg-[#050507] flex-shrink-0 flex flex-col border-r border-slate-800 overflow-x-hidden
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 lg:py-6 border-b border-slate-800 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <Link to="/" onClick={() => setSidebarOpen(false)}>
              <Logo size="small" />
            </Link>
            <p className="text-slate-500 text-sm mt-1">Admin Portal</p>
          </div>
          <button 
            className="lg:hidden p-2 text-slate-400 hover:text-white flex-shrink-0"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            <button className="sidebar-link active w-full text-left" data-testid="nav-dashboard">
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </button>
            <button className="sidebar-link w-full text-left" data-testid="nav-applications">
              <FileText size={20} />
              <span>Applications</span>
            </button>
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { navigate('/admin/site-editor'); setSidebarOpen(false); }}
              data-testid="nav-site-editor"
            >
              <Palette size={20} />
              <span>Site Editor</span>
            </button>
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { setShowPDFEditor(true); setSidebarOpen(false); }}
              data-testid="nav-pdf-editor"
            >
              <FileEdit size={20} />
              <span>PDF Editor</span>
            </button>
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { setShowDocCreator(true); setSidebarOpen(false); }}
              data-testid="nav-doc-creator"
            >
              <FilePlus size={20} />
              <span>Document Creator</span>
            </button>
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { setShowMarketEditor(true); setSidebarOpen(false); }}
              data-testid="nav-markets"
            >
              <MapPin size={20} />
              <span>Markets</span>
            </button>
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { setShowRateCards(true); setSidebarOpen(false); }}
              data-testid="nav-rate-cards"
            >
              <Receipt size={20} />
              <span>Rate Cards</span>
            </button>
            {quickLink.url && (
              <a 
                href={quickLink.url.startsWith('http') ? quickLink.url : `https://${quickLink.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-link w-full text-left"
                data-testid="nav-quick-link"
              >
                <ExternalLink size={20} />
                <span>{quickLink.label || 'Quick Link'}</span>
              </a>
            )}
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { setShowLogoDownload(true); setSidebarOpen(false); }}
              data-testid="nav-logo-download"
            >
              <Download size={20} />
              <span>Download Logo</span>
            </button>
            <button 
              className="sidebar-link w-full text-left" 
              onClick={() => { setShowSettings(true); setSidebarOpen(false); }}
              data-testid="nav-settings"
            >
              <Settings size={20} />
              <span>Settings</span>
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{user?.first_name} {user?.last_name}</p>
              <p className="text-slate-500 text-xs">Administrator</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
            data-testid="admin-logout"
          >
            <LogOut size={18} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        {/* Mobile Header */}
        <header className="lg:hidden bg-[#12121A] border-b border-slate-800 px-3 py-3 flex items-center justify-between sticky top-0 z-30">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 flex-shrink-0"
            aria-label="Open menu"
            data-testid="open-sidebar"
          >
            <Menu size={22} />
          </button>
          <div className="flex-1 flex justify-center px-2 overflow-hidden">
            <Logo size="small" />
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 p-2 flex-shrink-0">
            <LogOut size={20} />
          </Button>
        </header>

        <div className="p-4 sm:p-6 lg:p-8 pb-28 sm:pb-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <div 
              className={`bg-[#12121A] rounded-xl p-4 sm:p-6 border cursor-pointer transition-all hover:border-violet-500/30 ${filter === 'all' ? 'border-violet-500 ring-1 ring-violet-500/50' : 'border-slate-800'}`}
              onClick={() => setFilter('all')}
              data-testid="stat-total"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-800 rounded-lg flex items-center justify-center">
                  <FileText className="text-slate-400" size={24} />
                </div>
                <span className="text-2xl sm:text-3xl font-bold text-white">{stats.total}</span>
              </div>
              <p className="text-sm sm:text-base text-slate-500 font-medium">Total Applications</p>
            </div>

            <div 
              className={`bg-[#12121A] rounded-xl p-4 sm:p-6 border cursor-pointer transition-all hover:border-amber-500/30 ${filter === 'incomplete' ? 'border-amber-500 ring-1 ring-amber-500/50' : 'border-slate-800'}`}
              onClick={() => setFilter('incomplete')}
              data-testid="stat-incomplete"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="text-amber-400" size={24} />
                </div>
                <span className="text-2xl sm:text-3xl font-bold text-white">{stats.incomplete}</span>
              </div>
              <p className="text-sm sm:text-base text-slate-500 font-medium">Needs Review</p>
            </div>

            <div 
              className={`bg-[#12121A] rounded-xl p-4 sm:p-6 border cursor-pointer transition-all hover:border-emerald-500/30 ${filter === 'complete' ? 'border-emerald-500 ring-1 ring-emerald-500/50' : 'border-slate-800'}`}
              onClick={() => setFilter('complete')}
              data-testid="stat-complete"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-emerald-400" size={24} />
                </div>
                <span className="text-2xl sm:text-3xl font-bold text-white">{stats.complete}</span>
              </div>
              <p className="text-sm sm:text-base text-slate-500 font-medium">Complete</p>
            </div>

            <div 
              className={`bg-[#12121A] rounded-xl p-4 sm:p-6 border cursor-pointer transition-all hover:border-slate-600 ${filter === 'draft' ? 'border-slate-500 ring-1 ring-slate-500/50' : 'border-slate-800'}`}
              onClick={() => setFilter('draft')}
              data-testid="stat-drafts"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Clock className="text-slate-400" size={24} />
                </div>
                <span className="text-2xl sm:text-3xl font-bold text-white">{stats.drafts}</span>
              </div>
              <p className="text-sm sm:text-base text-slate-500 font-medium">Drafts</p>
            </div>
          </div>

          {/* Admin Documents Section */}
          {adminDocs.length > 0 && (
            <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <FolderOpen size={18} className="text-violet-400" />
                  My Documents
                </h2>
                <span className="text-xs text-slate-500">{adminDocs.length}/5</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {adminDocs.map(doc => (
                  <div key={doc.id} className="min-w-0 bg-[#0A0A0F] rounded-lg border border-slate-700 p-3 flex flex-col" data-testid={`admin-doc-${doc.id}`}>
                    <div className="flex items-center gap-2 mb-2 min-w-0">
                      <FileText className="text-violet-400 flex-shrink-0" size={16} />
                      <span className="text-sm text-white font-medium truncate">{doc.title}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2 truncate">{doc.filename}</p>
                    <a
                      href={`${API_URL}/admin/docs/${doc.id}/download`}
                      className="mt-auto text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      data-testid={`download-admin-doc-${doc.id}`}
                    >
                      <Download size={12} />
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bank Info Link Section */}
          <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 mb-6">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="text-base font-bold text-white flex items-center gap-2 min-w-0" style={{ fontFamily: 'Syne, sans-serif' }}>
                <Building2 size={18} className="text-violet-400 flex-shrink-0" />
                {editingBankTitle ? (
                  <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
                    <input
                      value={bankTitleInput}
                      onChange={(e) => setBankTitleInput(e.target.value)}
                      className="bg-[#0A0A0F] border border-slate-700 rounded px-2 py-1 text-white text-sm w-full sm:w-48"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && saveBankLinkTitle()}
                      data-testid="bank-title-input"
                    />
                    <Button size="sm" onClick={saveBankLinkTitle} className="btn-gradient text-white h-7 px-2 text-xs">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingBankTitle(false)} className="text-slate-400 h-7 px-2 text-xs">Cancel</Button>
                  </div>
                ) : (
                  <>
                    <span className="truncate">{bankLinkTitle}</span>
                    <button
                      onClick={() => { setBankTitleInput(bankLinkTitle); setEditingBankTitle(true); }}
                      className="text-slate-500 hover:text-violet-400 transition-colors flex-shrink-0"
                      data-testid="edit-bank-title"
                    >
                      <Pencil size={14} />
                    </button>
                  </>
                )}
              </h2>
              <Link2 size={16} className="text-slate-500" />
            </div>
            <p className="text-xs text-slate-500 mb-3">Share this link with technicians to collect their bank information for direct deposit.</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-[#0A0A0F] border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-cyan-400 font-mono truncate" data-testid="bank-info-url">
                {bankInfoUrl}
              </div>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(bankInfoUrl);
                  toast.success('Link copied to clipboard!');
                }}
                className="bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 border border-violet-500/30 flex-shrink-0"
                data-testid="copy-bank-link"
              >
                <Copy size={14} className="mr-1" />
                Copy
              </Button>
            </div>
          </div>

          {/* Applications Table */}
          <div className="bg-[#12121A] rounded-xl border border-slate-800">
            {/* Table Header */}
            <div className="p-4 sm:p-6 border-b border-slate-800">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Applications
                  </h2>
                  <p className="text-slate-500 text-sm">
                    {filter === 'all' ? 'All applications' : `Showing ${filter} applications`}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      placeholder="Search applicants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full sm:w-64 h-10 bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-600"
                      data-testid="search-applications"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={fetchData}
                    className="border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw size={18} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Table */}
            {loading && (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500 mx-auto"></div>
              </div>
            )}
            {!loading && filteredApplications.length === 0 && (
              <div className="p-12 text-center">
                <FileText className="mx-auto text-slate-600" size={48} />
                <p className="text-slate-500 mt-4">No applications found</p>
              </div>
            )}
            {!loading && filteredApplications.length > 0 && (
              <>
              <div className="md:hidden divide-y divide-slate-800" data-testid="applications-mobile-list">
                {filteredApplications.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    badge={getStatusBadge(app.status)}
                    onView={() => navigate(`/admin/applications/${app.id}`)}
                    onDelete={() => setShowDeleteConfirm(app)}
                  />
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.id} data-testid={`application-row-${app.id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center text-violet-300 font-semibold">
                              {app.data?.first_name?.[0] || 'U'}{app.data?.last_name?.[0] || ''}
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {app.data?.first_name || 'Unknown'} {app.data?.last_name || ''}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">{app.id?.slice(0, 8)}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <p className="text-slate-300">{app.user_email}</p>
                            <p className="text-slate-500 text-sm">{app.data?.phone || 'No phone'}</p>
                          </div>
                        </td>
                        <td>{getStatusBadge(app.status)}</td>
                        <td>
                          <p className="text-slate-400">
                            {new Date(app.updated_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/applications/${app.id}`)}
                              className="text-slate-400 hover:text-white hover:bg-slate-800"
                              data-testid={`view-application-${app.id}`}
                            >
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowDeleteConfirm(app)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-application-${app.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Logo Download Modal */}
      {showLogoDownload && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex overflow-y-auto p-4">
          <div className="relative max-w-md w-full m-auto pt-10">
            <button
              onClick={() => setShowLogoDownload(false)}
              className="absolute top-0 right-0 flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
              data-testid="close-logo-modal"
            >
              <X size={16} />
              <span className="text-sm">Close</span>
            </button>
            <LogoDownload />
          </div>
        </div>
      )}

      {/* PDF Editor Modal */}
      {showPDFEditor && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
            <Loader2 className="animate-spin text-violet-500" size={48} />
          </div>
        }>
          <PDFEditor onClose={() => setShowPDFEditor(false)} />
        </Suspense>
      )}

      {/* Market Editor Modal */}
      {showMarketEditor && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
            <Loader2 className="animate-spin text-violet-500" size={48} />
          </div>
        }>
          <MarketEditor onClose={() => setShowMarketEditor(false)} />
        </Suspense>
      )}

      {showRateCards && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
            <Loader2 className="animate-spin text-violet-500" size={48} />
          </div>
        }>
          <RateCardManager onClose={() => setShowRateCards(false)} />
        </Suspense>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#12121A] rounded-xl border border-slate-800 p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Delete Application?
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Are you sure you want to delete the application from{' '}
              <span className="text-white font-medium">
                {showDeleteConfirm.data?.first_name} {showDeleteConfirm.data?.last_name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(null)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteApplication(showDeleteConfirm.id)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Trash2 size={16} className="mr-2" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <AdminSettingsModal 
          onClose={() => setShowSettings(false)} 
          getAuthHeaders={getAuthHeaders}
          user={user}
          onQuickLinkUpdate={(url, label) => setQuickLink({ url, label })}
          onDocsUpdate={fetchAdminDocs}
        />
      )}
    </div>
  );
}

function ApplicationCard({ app, badge, onView, onDelete }) {
  return (
    <div className="p-4" data-testid={`application-card-${app.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center text-violet-300 font-semibold flex-shrink-0">
          {app.data?.first_name?.[0] || 'U'}{app.data?.last_name?.[0] || ''}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium text-white truncate">
              {app.data?.first_name || 'Unknown'} {app.data?.last_name || ''}
            </p>
            {badge}
          </div>
          <p className="text-sm text-slate-300 truncate">{app.user_email}</p>
          <p className="text-xs text-slate-500">
            {app.data?.phone || 'No phone'} · {new Date(app.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 pl-[52px]">
        <Button
          variant="outline"
          size="sm"
          onClick={onView}
          className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
          data-testid={`view-application-mobile-${app.id}`}
        >
          <Eye size={16} className="mr-1" />
          View
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          data-testid={`delete-application-mobile-${app.id}`}
        >
          <Trash2 size={16} className="mr-1" />
          Delete
        </Button>
      </div>
    </div>
  );
}

// Admin Settings Modal Component
function AdminSettingsModal({ onClose, getAuthHeaders, user, onQuickLinkUpdate, onDocsUpdate }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [linkData, setLinkData] = useState({ url: '', label: '' });
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, docsRes] = await Promise.all([
          axios.get(`${API_URL}/admin/profile`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/admin/docs`, { headers: getAuthHeaders() })
        ]);
        setLinkData({ url: profileRes.data.quick_link_url || '', label: profileRes.data.quick_link_label || '' });
        setDocs(docsRes.data || []);
      } catch { toast.error('Could not load settings'); }
    };
    fetchData();
  }, [getAuthHeaders]);

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (docs.length >= 5) { toast.error('Maximum 5 documents. Delete one first.'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);
      const res = await axios.post(`${API_URL}/admin/docs`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setDocs(prev => [res.data, ...prev]);
      onDocsUpdate?.();
      toast.success('Document uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API_URL}/admin/docs/${docId}`, { headers: getAuthHeaders() });
      setDocs(prev => prev.filter(d => d.id !== docId));
      onDocsUpdate?.();
      toast.success('Document deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleProfileSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/profile`, profile, { headers: getAuthHeaders() });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, { headers: getAuthHeaders() });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            Admin Settings
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0A0A0F] rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'profile' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-white'
            }`}
          >
            <User size={16} />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'password' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Lock size={16} />
            Password
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'link' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-white'
            }`}
          >
            <ExternalLink size={16} />
            Link
          </button>
          <button
            onClick={() => setActiveTab('docs')}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'docs' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-white'
            }`}
          >
            <FolderOpen size={16} />
            Docs
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm">First Name</Label>
                <Input
                  value={profile.first_name}
                  onChange={(e) => setProfile(p => ({ ...p, first_name: e.target.value }))}
                  className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                  data-testid="settings-first-name"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Last Name</Label>
                <Input
                  value={profile.last_name}
                  onChange={(e) => setProfile(p => ({ ...p, last_name: e.target.value }))}
                  className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                  data-testid="settings-last-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Email Address</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))}
                className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                data-testid="settings-email"
              />
            </div>
            <Button
              onClick={handleProfileSave}
              disabled={saving}
              className="w-full btn-gradient text-white"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Save Profile
            </Button>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm">Current Password</Label>
              <Input
                type="password"
                value={passwords.current_password}
                onChange={(e) => setPasswords(p => ({ ...p, current_password: e.target.value }))}
                className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                data-testid="settings-current-password"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">New Password</Label>
              <Input
                type="password"
                value={passwords.new_password}
                onChange={(e) => setPasswords(p => ({ ...p, new_password: e.target.value }))}
                className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                data-testid="settings-new-password"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Confirm New Password</Label>
              <Input
                type="password"
                value={passwords.confirm_password}
                onChange={(e) => setPasswords(p => ({ ...p, confirm_password: e.target.value }))}
                className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                data-testid="settings-confirm-password"
              />
            </div>
            <Button
              onClick={handlePasswordChange}
              disabled={saving || !passwords.current_password || !passwords.new_password}
              className="w-full btn-gradient text-white"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Change Password
            </Button>
          </div>
        )}

        {/* Quick Link Tab */}
        {activeTab === 'link' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Add a custom link that will appear in the dashboard sidebar for quick access.
            </p>
            <div>
              <Label className="text-slate-300 text-sm">Link Label</Label>
              <Input
                value={linkData.label}
                onChange={(e) => setLinkData(p => ({ ...p, label: e.target.value }))}
                placeholder="e.g. Company Portal, CRM, Google Drive"
                className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                data-testid="settings-link-label"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">URL</Label>
              <Input
                value={linkData.url}
                onChange={(e) => setLinkData(p => ({ ...p, url: e.target.value }))}
                placeholder="https://example.com"
                className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                data-testid="settings-link-url"
              />
            </div>
            <Button
              onClick={async () => {
                setSaving(true);
                try {
                  await axios.put(`${API_URL}/admin/profile`, {
                    quick_link_url: linkData.url,
                    quick_link_label: linkData.label
                  }, { headers: getAuthHeaders() });
                  toast.success('Quick link saved');
                  onQuickLinkUpdate?.(linkData.url, linkData.label);
                } catch { toast.error('Failed to save link'); }
                finally { setSaving(false); }
              }}
              disabled={saving}
              className="w-full btn-gradient text-white"
              data-testid="save-quick-link-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Save Quick Link
            </Button>
            {linkData.url && (
              <div className="p-3 bg-[#0A0A0F] rounded-lg border border-slate-700">
                <p className="text-xs text-slate-500 mb-1">Preview:</p>
                <a 
                  href={linkData.url.startsWith('http') ? linkData.url : `https://${linkData.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-2"
                >
                  <ExternalLink size={14} />
                  {linkData.label || linkData.url}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'docs' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Upload up to 5 documents for quick access on your dashboard.
            </p>
            
            {/* Upload button */}
            <div>
              <input type="file" id="admin-doc-upload" className="hidden" onChange={handleUploadDoc} data-testid="admin-doc-upload-input" />
              <Button
                onClick={() => document.getElementById('admin-doc-upload').click()}
                disabled={uploading || docs.length >= 5}
                className="w-full border-dashed border-2 border-slate-700 bg-transparent text-slate-300 hover:border-violet-500/50 hover:bg-violet-500/5"
                data-testid="upload-admin-doc-btn"
              >
                {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
                {docs.length >= 5 ? 'Maximum 5 documents reached' : 'Upload Document'}
              </Button>
            </div>

            {/* Document list */}
            {docs.length > 0 ? (
              <div className="space-y-2">
                {docs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-[#0A0A0F] rounded-lg border border-slate-700" data-testid={`settings-doc-${doc.id}`}>
                    <FileText className="text-violet-400 flex-shrink-0" size={18} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">{doc.title}</p>
                      <p className="text-xs text-slate-500">{(doc.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => handleDeleteDoc(doc.id)}
                      className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"
                      data-testid={`delete-settings-doc-${doc.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <FolderOpen size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No documents uploaded yet</p>
              </div>
            )}

            <p className="text-xs text-slate-600 text-center">{docs.length}/5 documents used</p>
          </div>
        )}
      </div>
    </div>
  );
}
